import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Calculator,
  Handshake,
  Percent,
  Calendar,
  Building2,
  UserCheck,
  FileText,
  DollarSign,
  ShieldAlert,
  Sparkles,
  Copy,
  Send,
  Eye,
  CheckCircle2,
  ExternalLink,
  Lock,
  Download
} from 'lucide-react';
import { PaymentAgreement, InstallmentRecord } from './PaymentAgreementTypes';
import { PaymentAgreementPdfService } from '@/services/paymentAgreementPdfService';
import { PaymentAgreementDatabaseService } from '@/services/paymentAgreementDatabaseService';
import { toast } from 'sonner';

interface DebtorOption {
  id?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  dias_mora?: number;
}

interface PaymentAgreementSimulatorProps {
  debtors: DebtorOption[];
  preselectedDebtor?: DebtorOption | null;
  copropiedadNombre?: string;
  copropiedadNit?: string;
  administradorNombre?: string;
  administradorTelefono?: string;
  administradorEmail?: string;
  administradorWhatsapp?: string;
  administradorHorario?: string;
  administradorOficina?: string;
  onSaveAgreement: (agreement: PaymentAgreement) => void;
  onOpenPortalPreview: (agreement: PaymentAgreement) => void;
}

export const PaymentAgreementSimulator: React.FC<PaymentAgreementSimulatorProps> = ({
  debtors,
  preselectedDebtor,
  copropiedadNombre = 'Conjunto Residencial Torres del Parque',
  copropiedadNit = '900.852.147-3',
  administradorNombre = 'Administración PH',
  administradorTelefono,
  administradorEmail,
  administradorWhatsapp,
  administradorHorario,
  administradorOficina,
  onSaveAgreement,
  onOpenPortalPreview
}) => {
  // Formulario del deudor
  const [selectedDebtorId, setSelectedDebtorId] = useState<string>('');
  const [unidadPrivada, setUnidadPrivada] = useState('');
  const [propietarioNombre, setPropietarioNombre] = useState('');
  const [propietarioId, setPropietarioId] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');

  // Parámetros financieros
  const [saldoCapital, setSaldoCapital] = useState<number>(1800000);
  const [saldoIntereses, setSaldoIntereses] = useState<number>(450000);

  // Parámetros de negociación interactiva
  const [condonacionInteresesPct, setCondonacionInteresesPct] = useState<number>(50); // % de condonación
  const [cuotaInicialPct, setCuotaInicialPct] = useState<number>(25); // % cuota inicial
  const [numeroCuotas, setNumeroCuotas] = useState<number>(6); // plazo en meses
  const [tasaFinanciacionMensual, setTasaFinanciacionMensual] = useState<number>(0); // 0% usualmente en PH
  const [diaCorteMensual, setDiaCorteMensual] = useState<number>(10); // día 10 de cada mes

  // Modo de visualización: Tabla Amortización vs. Acta Legal
  const [viewMode, setViewMode] = useState<'amortizacion' | 'acta'>('amortizacion');

  // Prellenar si viene preselectedDebtor
  useEffect(() => {
    if (preselectedDebtor) {
      setUnidadPrivada(preselectedDebtor.unidad_privada || '');
      setPropietarioNombre(preselectedDebtor.propietario_nombre || '');
      setPropietarioId(preselectedDebtor.propietario_id || '');
      setTelefono(preselectedDebtor.telefono || '');
      setEmail(preselectedDebtor.email || '');
      setSaldoCapital(preselectedDebtor.saldo_capital || 0);
      setSaldoIntereses(preselectedDebtor.saldo_intereses || 0);
      setSelectedDebtorId(preselectedDebtor.id || preselectedDebtor.unidad_privada);
    }
  }, [preselectedDebtor]);

  // Manejar cambio en dropdown de deudores registrados
  const handleSelectDebtor = (debtorKey: string) => {
    setSelectedDebtorId(debtorKey);
    const debtor = debtors.find(d => (d.id === debtorKey || d.unidad_privada === debtorKey));
    if (debtor) {
      setUnidadPrivada(debtor.unidad_privada);
      setPropietarioNombre(debtor.propietario_nombre);
      setPropietarioId(debtor.propietario_id || '');
      setTelefono(debtor.telefono || '');
      setEmail(debtor.email || '');
      setSaldoCapital(debtor.saldo_capital || 0);
      setSaldoIntereses(debtor.saldo_intereses || 0);
      toast.success(`Datos cargados para ${debtor.unidad_privada}`);
    }
  };

  // Cálculos financieros reactivos
  const financialCalculations = useMemo(() => {
    const deudaTotalInicial = saldoCapital + saldoIntereses;
    const interesesCondonados = Math.round(saldoIntereses * (condonacionInteresesPct / 100));
    const interesesEfectivos = Math.max(0, saldoIntereses - interesesCondonados);
    const deudaPactada = saldoCapital + interesesEfectivos;

    // Cuota inicial
    const cuotaInicialMonto = Math.round(deudaPactada * (cuotaInicialPct / 100));
    const saldoAFinanciar = Math.max(0, deudaPactada - cuotaInicialMonto);

    // Cuotas mensuales
    let valorCuotaMensual = 0;
    if (numeroCuotas > 0) {
      if (tasaFinanciacionMensual === 0) {
        valorCuotaMensual = Math.round(saldoAFinanciar / numeroCuotas);
      } else {
        const i = tasaFinanciacionMensual / 100;
        // Fórmula de cuota fija (anualidad): C = S * [i / (1 - (1+i)^-n)]
        valorCuotaMensual = Math.round((saldoAFinanciar * i) / (1 - Math.pow(1 + i, -numeroCuotas)));
      }
    }

    // Generar tabla de amortización con fechas proyectadas
    const tablaAmortizacion: InstallmentRecord[] = [];
    const today = new Date();
    let saldoRemanente = saldoAFinanciar;

    for (let c = 1; c <= numeroCuotas; c++) {
      const fechaVenc = new Date(today.getFullYear(), today.getMonth() + c, diaCorteMensual);
      const interesCuota = Math.round(saldoRemanente * (tasaFinanciacionMensual / 100));
      const capitalCuota = c === numeroCuotas ? saldoRemanente : Math.round(valorCuotaMensual - interesCuota);
      saldoRemanente = Math.max(0, saldoRemanente - capitalCuota);

      tablaAmortizacion.push({
        numero: c,
        fecha_vencimiento: fechaVenc.toISOString().split('T')[0],
        capital: capitalCuota,
        interes: interesCuota,
        total_cuota: capitalCuota + interesCuota,
        estado: 'pendiente'
      });
    }

    // Fecha límite para cuota inicial (ej. 5 días a partir de hoy)
    const fechaLimiteInicial = new Date();
    fechaLimiteInicial.setDate(fechaLimiteInicial.getDate() + 5);

    return {
      deudaTotalInicial,
      interesesCondonados,
      interesesEfectivos,
      deudaPactada,
      cuotaInicialMonto,
      saldoAFinanciar,
      valorCuotaMensual,
      tablaAmortizacion,
      fechaLimiteInicial: fechaLimiteInicial.toISOString().split('T')[0]
    };
  }, [saldoCapital, saldoIntereses, condonacionInteresesPct, cuotaInicialPct, numeroCuotas, tasaFinanciacionMensual, diaCorteMensual]);

  // Generar objeto de acuerdo completo
  const buildCurrentAgreement = (): PaymentAgreement => {
    const token = `tok_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
    const id = `AGR-${Date.now().toString().slice(-6)}`;
    const origin = window.location.origin;
    const slug = PaymentAgreementPdfService.slugifyConjunto(copropiedadNombre);
    const portalUrl = `${origin}/portal-deudor/${slug}/${token}`;
    const defaultSettings = PaymentAgreementDatabaseService.getAdminContactSettings();

    return {
      id,
      copropiedad_id: 'default',
      copropiedad_nombre: copropiedadNombre,
      copropiedad_nit: copropiedadNit,
      copropiedad_logo_url: defaultSettings.copropiedad_logo_url || '',
      copropiedad_logo_data: defaultSettings.copropiedad_logo_data || '',
      administrador_nombre: administradorNombre,
      administrador_telefono: administradorTelefono || defaultSettings.administrador_telefono,
      administrador_email: administradorEmail || defaultSettings.administrador_email,
      administrador_whatsapp: administradorWhatsapp || defaultSettings.administrador_whatsapp,
      administrador_horario: administradorHorario || defaultSettings.administrador_horario,
      administrador_oficina: administradorOficina || defaultSettings.administrador_oficina,
      unidad_privada: unidadPrivada || 'Torre 1 Apto 101',
      propietario_nombre: propietarioNombre || 'Propietario Registrado',
      propietario_id: propietarioId || '1020304050',
      telefono: telefono || '3001234567',
      email: email || 'propietario@email.com',

      deuda_total_inicial: financialCalculations.deudaTotalInicial,
      capital_original: saldoCapital,
      intereses_originales: saldoIntereses,
      porcentaje_condonacion_intereses: condonacionInteresesPct,
      intereses_condonados: financialCalculations.interesesCondonados,
      deuda_pactada: financialCalculations.deudaPactada,

      cuota_inicial_monto: financialCalculations.cuotaInicialMonto,
      cuota_inicial_porcentaje: cuotaInicialPct,
      cuota_inicial_pagada: false,
      cuota_inicial_fecha_limite: financialCalculations.fechaLimiteInicial,

      saldo_a_financiar: financialCalculations.saldoAFinanciar,
      numero_cuotas: numeroCuotas,
      valor_cuota_mensual: financialCalculations.valorCuotaMensual,
      tasa_financiacion_mensual: tasaFinanciacionMensual,
      dia_pago_mensual: diaCorteMensual,
      fecha_inicio: new Date().toISOString().split('T')[0],

      estado: 'enviado_al_deudor',
      cuotas_pagadas: 0,
      cuotas_totales: numeroCuotas,
      total_recaudado: 0,
      saldo_restante: financialCalculations.deudaPactada,
      proximo_vencimiento: financialCalculations.fechaLimiteInicial,

      magic_token: token,
      portal_url: portalUrl,
      cuotas_detalle: financialCalculations.tablaAmortizacion,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  };

  const handleFormalize = () => {
    if (!unidadPrivada.trim() || !propietarioNombre.trim()) {
      toast.error('La Unidad Privada y el Nombre del Propietario son obligatorios.');
      return;
    }
    if (financialCalculations.deudaPactada <= 0) {
      toast.error('El valor pactado de la deuda debe ser mayor a cero.');
      return;
    }

    const newAgreement = buildCurrentAgreement();
    onSaveAgreement(newAgreement);
    toast.success(`¡Acuerdo para ${unidadPrivada} formalizado con éxito!`);
  };

  const handlePreviewPortal = () => {
    const current = buildCurrentAgreement();
    onOpenPortalPreview(current);
  };

  const handleSendWhatsAppProposal = () => {
    const agreement = buildCurrentAgreement();
    PaymentAgreementPdfService.shareAgreementViaWhatsApp(agreement);
  };

  // Texto formal del contrato para previsualización
  const actaLegalTexto = useMemo(() => {
    return `ACTA DE ACUERDO DE TRANSACCIÓN Y RECONOCIMIENTO DE DEUDA
RÉGIMEN DE PROPIEDAD HORIZONTAL (LEY 675 DE 2001) Y CÓDIGO CIVIL (ARTS. 2469 Y SS.)

En la ciudad de Bogotá D.C., a los ${new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}, entre los suscritos:

1. De una parte, ${administradorNombre}, mayor de edad, identificado como aparece al pie de su firma, actuando en calidad de Administrador y Representante Legal de la persona jurídica ${copropiedadNombre}, con NIT ${copropiedadNit}, en adelante denominada LA COPROPIEDAD.
2. De otra parte, ${propietarioNombre || '[NOMBRE DEL COPROPIETARIO]'}, identificado(a) con C.C./NIT ${propietarioId || '[CÉDULA]'}, en su condición de propietario(a) del inmueble identificado como ${unidadPrivada || '[UNIDAD PRIVADA]'}, en adelante denominado(a) EL DEUDOR.

Hemos convenido celebrar el presente ACUERDO DE TRANSACCIÓN EXTRAJUDICIAL, sujeto a las siguientes cláusulas:

PRIMERA. RECONOCIMIENTO DE LA OBLIGACIÓN: EL DEUDOR reconoce de manera libre, voluntaria, expresa, clara y exigible que adeuda a LA COPROPIEDAD por concepto de expensas comunes de administración e intereses moratorios acumulados la suma inicial de $${financialCalculations.deudaTotalInicial.toLocaleString('es-CO')} COP.

SEGUNDA. BENEFICIO CONDICIONADO DE CONDONACIÓN: Con el ánimo de finiquitar controversias y precaver litigios, LA COPROPIEDAD autoriza condonar el ${condonacionInteresesPct}% de los intereses de mora devengados, equivalente a $${financialCalculations.interesesCondonados.toLocaleString('es-CO')} COP. Dicha condonación queda estrictamente supeditada al cumplimiento íntegro y oportuno de todas las cuotas aquí acordadas. Por consiguiente, el saldo neto pactado para pago es la suma de $${financialCalculations.deudaPactada.toLocaleString('es-CO')} COP.

TERCERA. PLAN DE AMORTIZACIÓN Y PAGOS: EL DEUDOR se compromete a cancelar la suma pactada en la siguiente forma:
1. Una CUOTA INICIAL por valor de $${financialCalculations.cuotaInicialMonto.toLocaleString('es-CO')} COP, pagadera a más tardar el día ${financialCalculations.fechaLimiteInicial}.
2. El saldo remanente de $${financialCalculations.saldoAFinanciar.toLocaleString('es-CO')} COP, financiado en ${numeroCuotas} cuotas mensuales consecutivas de $${financialCalculations.valorCuotaMensual.toLocaleString('es-CO')} COP cada una, pagaderas el día ${diaCorteMensual} de cada mes calendario.

CUARTA. MÉRITO EJECUTIVO Y CLÁUSULA ACELERATORIA: Las partes convienen expresamente que el presente documento presta MÉRITO EJECUTIVO PLENO en los términos del artículo 422 del Código General del Proceso y el artículo 48 de la Ley 675 de 2001. EL INCUMPLIMIENTO EN EL PAGO DE CUALQUIERA DE LAS CUOTAS PACTADAS O DE LAS EXPENSAS ORDINARIAS FUTURAS acarreará la pérdida automática del beneficio de condonación de intereses concedido en la cláusula segunda, haciéndose exigible la totalidad de la deuda original insoluta, facultando a LA COPROPIEDAD para iniciar o reactivar de inmediato el proceso judicial ejecutivo con solicitud de medidas cautelares de embargo y secuestro sobre el inmueble y cuentas bancarias, sin necesidad de requerimiento previo alguno para constituir en mora, a los cuales EL DEUDOR renuncia expresamente.

QUINTA. EFECTOS DE COSA JUZGADA: El presente acuerdo surte los efectos de cosa juzgada de conformidad con el artículo 2483 del Código Civil Colombiano.

Para constancia de aceptación, se suscribe mediante canal y validación electrónica (Ley 527 de 1999).`;
  }, [
    administradorNombre, copropiedadNombre, copropiedadNit, propietarioNombre, propietarioId,
    unidadPrivada, financialCalculations, condonacionInteresesPct, numeroCuotas, diaCorteMensual
  ]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* ── COLUMNA IZQUIERDA: CONTROLES DEL SIMULADOR FINANCIERO ── */}
      <Card className="lg:col-span-5 shadow-sm border">
        <CardHeader className="pb-3 border-b bg-gradient-to-r from-emerald-50/60 to-teal-50/40 dark:from-emerald-950/20 dark:to-teal-950/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-600 text-white shadow-sm">
                <Calculator className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-base font-bold text-slate-900 dark:text-white">
                  Simulador de Acuerdos
                </CardTitle>
                <CardDescription className="text-xs">
                  Negociación interactiva de cuotas y condonaciones
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-300 text-[10px]">
              Ley 675 / CGP
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pt-4 text-xs">
          {/* Autocompletar desde Directorio */}
          <div className="space-y-1.5 p-3 rounded-lg border bg-muted/30">
            <Label className="text-xs font-semibold flex items-center justify-between text-slate-800 dark:text-slate-200">
              <span className="flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                Vincular Inmueble del Directorio
              </span>
              <span className="text-[10px] text-muted-foreground font-mono">{debtors.length} disponibles</span>
            </Label>
            <Select value={selectedDebtorId} onValueChange={handleSelectDebtor}>
              <SelectTrigger className="h-8 text-xs bg-white dark:bg-slate-900">
                <SelectValue placeholder="Seleccione unidad (ej. Torre 2 Apto 504)..." />
              </SelectTrigger>
              <SelectContent className="max-h-56">
                {debtors.map((d) => {
                  const tot = (d.saldo_capital || 0) + (d.saldo_intereses || 0);
                  return (
                    <SelectItem key={d.id || d.unidad_privada} value={d.id || d.unidad_privada}>
                      {d.unidad_privada} — {d.propietario_nombre} (${tot.toLocaleString('es-CO')})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Datos Básicos de la Unidad */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px] font-medium">Unidad Privada</Label>
              <Input
                placeholder="Torre 2 Apto 504"
                value={unidadPrivada}
                onChange={(e) => setUnidadPrivada(e.target.value)}
                className="text-xs h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-medium">Propietario / Deudor</Label>
              <Input
                placeholder="Nombre completo"
                value={propietarioNombre}
                onChange={(e) => setPropietarioNombre(e.target.value)}
                className="text-xs h-8"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px] font-medium">WhatsApp / Teléfono</Label>
              <Input
                placeholder="3001234567"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                className="text-xs h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-medium">Correo Electrónico</Label>
              <Input
                placeholder="propietario@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="text-xs h-8"
              />
            </div>
          </div>

          {/* Saldos Iniciales */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1">
              <Label className="text-[11px] font-medium flex items-center justify-between">
                <span>Saldo Capital ($)</span>
              </Label>
              <Input
                type="number"
                value={saldoCapital}
                onChange={(e) => setSaldoCapital(parseFloat(e.target.value) || 0)}
                className="text-xs h-8 font-semibold"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-medium flex items-center justify-between">
                <span>Intereses Causados ($)</span>
              </Label>
              <Input
                type="number"
                value={saldoIntereses}
                onChange={(e) => setSaldoIntereses(parseFloat(e.target.value) || 0)}
                className="text-xs h-8 font-semibold text-rose-600 dark:text-rose-400"
              />
            </div>
          </div>

          <div className="pt-2 border-t space-y-4">
            {/* Slider 1: Condonación de Intereses */}
            <div className="space-y-1.5 p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-emerald-900 dark:text-emerald-300 flex items-center gap-1.5">
                  <Percent className="h-3.5 w-3.5 text-emerald-600" />
                  Condonación de Intereses de Mora
                </span>
                <Badge className="bg-emerald-600 text-white font-mono text-[11px]">
                  {condonacionInteresesPct}% OFF
                </Badge>
              </div>
              <Slider
                value={[condonacionInteresesPct]}
                onValueChange={(val) => setCondonacionInteresesPct(val[0])}
                max={100}
                step={5}
                className="py-1"
              />
              <div className="flex justify-between text-[11px] text-muted-foreground pt-0.5">
                <span>Ahorro para deudor: <strong>${financialCalculations.interesesCondonados.toLocaleString('es-CO')}</strong></span>
                <span>Interés a cobrar: <strong>${financialCalculations.interesesEfectivos.toLocaleString('es-CO')}</strong></span>
              </div>
            </div>

            {/* Slider 2: Cuota Inicial */}
            <div className="space-y-1.5 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5 text-amber-600" />
                  Cuota Inicial Requerida
                </span>
                <Badge variant="outline" className="border-amber-400 text-amber-800 dark:text-amber-300 font-mono text-[11px]">
                  {cuotaInicialPct}% (${financialCalculations.cuotaInicialMonto.toLocaleString('es-CO')})
                </Badge>
              </div>
              <Slider
                value={[cuotaInicialPct]}
                onValueChange={(val) => setCuotaInicialPct(val[0])}
                min={5}
                max={60}
                step={5}
                className="py-1"
              />
              <span className="text-[10px] text-muted-foreground block">
                Saldo remanente a financiar: <strong>${financialCalculations.saldoAFinanciar.toLocaleString('es-CO')}</strong>
              </span>
            </div>

            {/* Plazo y Día de Pago */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] font-medium">Plazo de Financiación</Label>
                <Select value={String(numeroCuotas)} onValueChange={(val) => setNumeroCuotas(parseInt(val, 10))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 cuota (30 días)</SelectItem>
                    <SelectItem value="2">2 cuotas (60 días)</SelectItem>
                    <SelectItem value="3">3 cuotas (90 días)</SelectItem>
                    <SelectItem value="4">4 cuotas (120 días)</SelectItem>
                    <SelectItem value="6">6 cuotas (Semestral)</SelectItem>
                    <SelectItem value="8">8 cuotas</SelectItem>
                    <SelectItem value="10">10 cuotas</SelectItem>
                    <SelectItem value="12">12 cuotas (1 Año)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] font-medium">Día de Corte Mensual</Label>
                <Select value={String(diaCorteMensual)} onValueChange={(val) => setDiaCorteMensual(parseInt(val, 10))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Día 5 de cada mes</SelectItem>
                    <SelectItem value="10">Día 10 de cada mes</SelectItem>
                    <SelectItem value="15">Día 15 de cada mes</SelectItem>
                    <SelectItem value="20">Día 20 de cada mes</SelectItem>
                    <SelectItem value="30">Día 30 de cada mes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Botonera de Formalización */}
          <div className="pt-3 border-t space-y-2">
            <Button
              onClick={handleFormalize}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 shadow gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              Guardar y Formalizar Acuerdo
            </Button>

            <div className="grid grid-cols-3 gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const current = buildCurrentAgreement();
                  PaymentAgreementPdfService.downloadAgreementPdf(current);
                }}
                className="text-xs h-8 gap-1 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200 hover:bg-muted font-semibold"
                title="Descargar Borrador Oficial en PDF"
              >
                <Download className="h-3.5 w-3.5 text-emerald-600" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviewPortal}
                className="text-xs h-8 gap-1 border-emerald-300 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50"
              >
                <Eye className="h-3.5 w-3.5" />
                Portal
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendWhatsAppProposal}
                className="text-xs h-8 gap-1 border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-semibold"
              >
                <Send className="h-3.5 w-3.5 text-emerald-600" />
                WhatsApp
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── COLUMNA DERECHA: RESULTADO EN VIVO (TABLA AMORTIZACIÓN Y ACTA JURÍDICA) ── */}
      <div className="lg:col-span-7 space-y-4">
        {/* Banner de Impacto Financiero en Vivo */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border shadow-xs bg-slate-900 text-white">
            <CardContent className="p-3">
              <span className="text-[10px] text-slate-400 block font-medium uppercase tracking-wider">
                Deuda Pactada
              </span>
              <span className="text-lg font-black text-emerald-400">
                ${financialCalculations.deudaPactada.toLocaleString('es-CO')}
              </span>
              <span className="text-[10px] text-slate-400 block mt-0.5">
                Antes: ${financialCalculations.deudaTotalInicial.toLocaleString('es-CO')}
              </span>
            </CardContent>
          </Card>

          <Card className="border shadow-xs bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900">
            <CardContent className="p-3">
              <span className="text-[10px] text-emerald-700 dark:text-emerald-400 block font-medium uppercase tracking-wider">
                Cuota Inicial ({cuotaInicialPct}%)
              </span>
              <span className="text-lg font-black text-emerald-900 dark:text-emerald-200">
                ${financialCalculations.cuotaInicialMonto.toLocaleString('es-CO')}
              </span>
              <span className="text-[10px] text-emerald-600 block mt-0.5">
                Límite: {financialCalculations.fechaLimiteInicial}
              </span>
            </CardContent>
          </Card>

          <Card className="border shadow-xs bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900">
            <CardContent className="p-3">
              <span className="text-[10px] text-amber-800 dark:text-amber-400 block font-medium uppercase tracking-wider">
                {numeroCuotas} Cuotas Mensuales
              </span>
              <span className="text-lg font-black text-amber-900 dark:text-amber-200">
                ${financialCalculations.valorCuotaMensual.toLocaleString('es-CO')}
              </span>
              <span className="text-[10px] text-amber-700 block mt-0.5">
                Cada día {diaCorteMensual} del mes
              </span>
            </CardContent>
          </Card>
        </div>

        {/* Selector de Vista: Amortización vs. Acta Jurídica */}
        <Card className="shadow-sm border">
          <CardHeader className="pb-2 border-b flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'amortizacion' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('amortizacion')}
                className={`text-xs h-7 gap-1.5 ${viewMode === 'amortizacion' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                <Calendar className="h-3.5 w-3.5" />
                Tabla de Amortización ({numeroCuotas} Cuotas)
              </Button>
              <Button
                variant={viewMode === 'acta' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('acta')}
                className={`text-xs h-7 gap-1.5 ${viewMode === 'acta' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                <FileText className="h-3.5 w-3.5" />
                Acta Jurídica (Mérito Ejecutivo)
              </Button>
            </div>

            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const current = buildCurrentAgreement();
                  PaymentAgreementPdfService.downloadAgreementPdf(current);
                }}
                className="text-xs h-7 gap-1 border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-semibold"
                title="Descargar Contrato en Formato PDF Oficial"
              >
                <Download className="h-3 w-3" /> Descargar PDF
              </Button>

              {viewMode === 'acta' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(actaLegalTexto);
                    toast.success('Acta legal copiada al portapapeles.');
                  }}
                  className="text-xs h-7 gap-1"
                >
                  <Copy className="h-3 w-3" /> Copiar Acta
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {viewMode === 'amortizacion' ? (
              <div className="overflow-x-auto max-h-96">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/40">
                      <TableHead className="w-16"># Cuota</TableHead>
                      <TableHead>Fecha Vencimiento</TableHead>
                      <TableHead>Capital</TableHead>
                      <TableHead>Interés</TableHead>
                      <TableHead className="font-bold">Total Cuota</TableHead>
                      <TableHead className="text-right">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Fila Cuota Inicial */}
                    <TableRow className="text-xs bg-amber-50/40 dark:bg-amber-950/20 font-medium">
                      <TableCell className="font-bold text-amber-900 dark:text-amber-200">Inicial</TableCell>
                      <TableCell>{financialCalculations.fechaLimiteInicial} (5 días)</TableCell>
                      <TableCell>${financialCalculations.cuotaInicialMonto.toLocaleString('es-CO')}</TableCell>
                      <TableCell>$0</TableCell>
                      <TableCell className="font-bold text-amber-900 dark:text-amber-200">
                        ${financialCalculations.cuotaInicialMonto.toLocaleString('es-CO')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px]">
                          Requerida
                        </Badge>
                      </TableCell>
                    </TableRow>

                    {/* Filas Cuotas Financiadas */}
                    {financialCalculations.tablaAmortizacion.map((cuota) => (
                      <TableRow key={cuota.numero} className="text-xs hover:bg-muted/30">
                        <TableCell className="font-bold">Cuota #{cuota.numero}</TableCell>
                        <TableCell>{cuota.fecha_vencimiento}</TableCell>
                        <TableCell>${cuota.capital.toLocaleString('es-CO')}</TableCell>
                        <TableCell>${cuota.interes.toLocaleString('es-CO')}</TableCell>
                        <TableCell className="font-bold text-foreground">
                          ${cuota.total_cuota.toLocaleString('es-CO')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300">
                            Pendiente
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-4 sm:p-5 text-xs text-slate-800 dark:text-slate-200 font-mono whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto bg-slate-50 dark:bg-slate-900/60 rounded-b-lg">
                {actaLegalTexto}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Garantías Legales y Cláusula Aceleratoria Alert */}
        <div className="p-3.5 rounded-lg border border-orange-200 dark:border-orange-900/50 bg-orange-50/70 dark:bg-orange-950/20 flex items-start gap-3 text-xs text-orange-950 dark:text-orange-200">
          <ShieldAlert className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="font-bold text-orange-900 dark:text-orange-300 block">
              Cláusula Aceleratoria y Mérito Ejecutivo Pleno (Ley 675 Art. 48 &amp; CGP Art. 422)
            </span>
            <p className="text-[11px] leading-relaxed text-orange-900/80 dark:text-orange-200/80">
              Al formalizar este acuerdo, si el copropietario entra en mora en una sola cuota mensual, el beneficio de condonación de intereses pierde automáticamente su vigencia y la administración queda facultada para radicar de inmediato la demanda ejecutiva por el 100% de la deuda histórica con medidas de embargo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PaymentAgreementSimulator;
